import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

type Metric = {
  id: string;
  label: string;
  target: string;
  description: string;
  color: string;
  icon: string;
};

const METRICS: Metric[] = [
  {
    id: "first-build",
    label: "Time to First Build",
    target: "< 5 min",
    description: "From workspace creation to a complete AAOS build pipeline execution",
    color: "#4ade80",
    icon: "⚡",
  },
  {
    id: "onboarding",
    label: "Developer Onboarding",
    target: "< 5 min",
    description: "New engineer to productive development environment with all tools configured",
    color: "#22d3ee",
    icon: "🚀",
  },
  {
    id: "faster-fix",
    label: "Faster to Code & Fix",
    target: "Baseline in pilot",
    description: "Measure build iteration speed, test feedback loops, and time from commit to validated result",
    color: "#60a5fa",
    icon: "⏱",
  },
  {
    id: "easier-fix",
    label: "Easier to Code & Fix",
    target: "Baseline in pilot",
    description: "Developer satisfaction, tooling friction points, and context-switching overhead",
    color: "#a78bfa",
    icon: "✦",
  },
];

export function OnboardingKPIsSlide() {
  const svgW = 1100;
  const cardW = 480;
  const cardH = 90;
  const cardGap = 16;
  const colGap = 24;
  const cols = 2;
  const rows = 2;
  const totalW = cardW * cols + colGap;
  const startX = (svgW - totalW) / 2;
  const startY = 10;

  const calloutY = startY + rows * (cardH + cardGap) + 14;
  const calloutH = 50;
  const calloutW = totalW;
  const calloutX = startX;

  const svgH = calloutY + calloutH + 10;

  return (
    <Box w="100%" maw={1400} px="xl">
      <Stack gap="md">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <SlideTitle
            title="Onboarding & Developer Productivity"
            subtitle="Key metrics for developer experience — baselines and targets established during the pilot"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "62vh" }}
            aria-label="Developer productivity metrics grid"
          >
            {METRICS.map((metric, i) => {
              const col = i % cols;
              const row = Math.floor(i / cols);
              const x = startX + col * (cardW + colGap);
              const y = startY + row * (cardH + cardGap);

              return (
                <motion.g
                  key={metric.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.12, duration: 0.4 }}
                >
                  <rect
                    x={x}
                    y={y}
                    width={cardW}
                    height={cardH}
                    rx={10}
                    fill="rgba(255,255,255,0.025)"
                    stroke={metric.color}
                    strokeWidth={1.5}
                    strokeOpacity={0.15}
                  />

                  {/* Left accent bar */}
                  <rect x={x} y={y} width={3} height={cardH} rx={1.5} fill={metric.color} opacity={0.5} />

                  {/* Icon */}
                  <text
                    x={x + 20}
                    y={y + 30}
                    fontSize={18}
                    fontFamily="inherit"
                  >
                    {metric.icon}
                  </text>

                  {/* Label */}
                  <text
                    x={x + 48}
                    y={y + 28}
                    fontSize={13}
                    fill="rgba(255,255,255,0.9)"
                    fontWeight={700}
                    fontFamily="inherit"
                  >
                    {metric.label}
                  </text>

                  {/* Target badge */}
                  <rect
                    x={x + cardW - 140}
                    y={y + 14}
                    width={124}
                    height={22}
                    rx={11}
                    fill={metric.color}
                    opacity={0.12}
                  />
                  <text
                    x={x + cardW - 78}
                    y={y + 29}
                    textAnchor="middle"
                    fontSize={10}
                    fill={metric.color}
                    fontWeight={600}
                    fontFamily="'Geist Mono', monospace"
                  >
                    {metric.target}
                  </text>

                  {/* Description */}
                  <foreignObject x={x + 20} y={y + 42} width={cardW - 40} height={40}>
                    <div
                      // @ts-expect-error xmlns
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{
                        fontSize: 10.5,
                        lineHeight: 1.4,
                        color: "rgba(255,255,255,0.5)",
                        fontFamily: "inherit",
                      }}
                    >
                      {metric.description}
                    </div>
                  </foreignObject>
                </motion.g>
              );
            })}

            {/* Pilot callout */}
            <motion.g
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.4 }}
            >
              <rect
                x={calloutX}
                y={calloutY}
                width={calloutW}
                height={calloutH}
                rx={8}
                fill="rgba(96,165,250,0.04)"
                stroke="rgba(96,165,250,0.15)"
                strokeWidth={1}
              />
              <text
                x={calloutX + 20}
                y={calloutY + 22}
                fontSize={11}
                fill="#60a5fa"
                fontWeight={700}
                fontFamily="inherit"
              >
                Pilot Objective
              </text>
              <text
                x={calloutX + 20}
                y={calloutY + 39}
                fontSize={10.5}
                fill="rgba(255,255,255,0.5)"
                fontFamily="inherit"
              >
                Establish measurement baselines with your team during the 3-month pilot, then set targets for production rollout.
              </text>
            </motion.g>
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
